import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiTrash2,
  FiEdit,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";
import ViewP2PSellOrderModal from "./ViewP2PSellOrderModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import UpdateSellOrderModal from "./UpdateSellOrderModal";

function AllSellOrders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Filtering States
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  const [keyWord, setKeyWord] = useState("");
  const [status, setStatus] = useState("");
  const [walletType, setWalletType] = useState("");

  // View Modal States
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenEditModal = (item) => {
    setEditData(item);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  const handleDeleteClick = (id) => {
    setDeleteModal({ show: true, id: id });
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await makeRequestAuth("post", "/admin/deleteP2POrder", {
        id: deleteModal.id,
      });
      showSuccessToast("Order deleted successfully");
      fetchOrders(currentPage);
      setDeleteModal({ show: false, id: null });
    } catch (err) {
      showErrorToast(err.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchOrders = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth(
          "get",
          "/admin/getP2PTransactionList",
          {
            page,
            limit: pageSize,
            keyWord,
            txType: "SELL", // Hardcoded for Sell Orders
            status,
            walletType,
          },
        );

        const payload = response?.data || {};
        setOrders(payload.data || []);
        setTotal(payload.total || 0);
        setTotalPages(payload.pages || 1);
        setCurrentPage(payload.page || 1);
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch Sell orders");
      } finally {
        setIsLoading(false);
      }
    },
    [keyWord, status, walletType, pageSize],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchOrders(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [keyWord, status, walletType, fetchOrders]);

  const getStatusStyle = (statusVal) => {
    switch (statusVal) {
      case "approve":
        return "bg-emerald-100 text-emerald-600 border-emerald-200";
      case "reject":
        return "bg-red-100 text-red-600 border-red-200";
      case "requested":
        return "bg-blue-100 text-blue-600 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-600 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const handleOpenViewModal = (item) => {
    setSelectedTransaction(item);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedTransaction(null);
  };

  if (isLoading && orders.length === 0) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Sell Orders List
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total {total} sell orders found
            </p>
          </div>

          <div className="flex flex-wrap gap-3 w-full md:w-auto">
            <Link to="/p2p/sell-order/create">
              <button className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-rose-500 text-white px-5 py-2 rounded-xl font-medium shadow-lg hover:scale-[1.02] transition">
                <FiPlus size={18} /> Add Sell Order
              </button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search hash or amount..."
              value={keyWord}
              onChange={(e) => setKeyWord(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <select
            value={walletType}
            onChange={(e) => setWalletType(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            <option value="">All Wallets</option>
            <option value="USDT">USDT</option>
            <option value="TOKEN">TOKEN</option>
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="requested">Requested</option>
            <option value="approve">Approved</option>
            <option value="reject">Rejected</option>
          </select>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6">OrderId</th>
                  <th className="py-4 px-6">Name</th>
                  <th className="py-4 px-6 text-center">Wallet</th>
                  <th className="py-4 px-6 text-right">Amount</th>
                  <th className="py-4 px-6 text-right">Rate</th>
                  <th className="py-4 px-6 text-center">Creator</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-center">View</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-500">
                      No Sell Orders Found
                    </td>
                  </tr>
                ) : (
                  orders.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition"
                    >
                      <td className="py-4 px-6 font-medium">{item.orderId}</td>
                      <td className="py-4 px-6 font-medium">
                        {item.name}
                        <div className="text-xs text-gray-500 mt-1 font-mono">
                          {item?.orderId ?? "NA"}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center font-medium">
                        {item.walletType}
                      </td>
                      <td className="py-4 px-6 text-right font-bold">
                        {item.amount.toLocaleString()}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-600 dark:text-gray-300">
                        ₹{item.rate}
                      </td>
                      <td className="py-4 px-6 text-center text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs border ${item.creatorModel === "Admin" ? "border-purple-300 text-purple-600 bg-purple-50 dark:bg-purple-900/20" : "border-gray-300 text-gray-600 bg-gray-50 dark:bg-gray-700"}`}
                        >
                          {item.creatorModel}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border uppercase tracking-wider ${getStatusStyle(item.status)}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          {/* View Button */}
                          <button
                            onClick={() => handleOpenViewModal(item)}
                            title="View Details"
                            className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white rounded-lg transition-all duration-200 shadow-sm"
                          >
                            <FiEye size={18} />
                          </button>

                          {/* Edit Button */}
                          <button
                            onClick={() => handleOpenEditModal(item)}
                            title="Edit Order"
                            className="p-2 text-amber-600 bg-amber-50 hover:bg-amber-600 hover:text-white dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-600 dark:hover:text-white rounded-lg transition-all duration-200 shadow-sm"
                          >
                            <FiEdit size={18} />{" "}
                            {/* Make sure to import FiEdit from react-icons/fi */}
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteClick(item._id)}
                            title="Delete Order"
                            className="p-2 text-red-600 bg-red-50 hover:bg-red-600 hover:text-white dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white rounded-lg transition-all duration-200 shadow-sm"
                          >
                            <FiTrash2 size={18} />{" "}
                            {/* Make sure to import FiTrash2 from react-icons/fi */}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => fetchOrders(currentPage - 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              >
                <FiChevronLeft size={20} />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => fetchOrders(currentPage + 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
              >
                <FiChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ViewP2PSellOrderModal
        isOpen={showViewModal}
        onClose={handleCloseViewModal}
        transaction={selectedTransaction}
      />
      <UpdateSellOrderModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        data={editData}
        refresh={() => fetchOrders(currentPage)}
      />
      <DeleteConfirmModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, id: null })}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

export default AllSellOrders;
